import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface LoginProps {
  onLogin: (password: string) => boolean;
  isAuthenticated: boolean;
}

const Login: React.FC<LoginProps> = ({ onLogin, isAuthenticated }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to admin if already authenticated
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!password.trim()) {
      setError('Password is required');
      return;
    }
    
    const success = onLogin(password);
    if (success) {
      navigate('/admin');
    } else {
      setError('Invalid password');
      setPassword('');
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-container">
        <h2>Admin Login</h2>
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="login-error">{error}</div>}
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              autoComplete="current-password"
            />
          </div>
          <button type="submit" className="login-button">Login</button>
        </form>
        <div className="login-footer">
          <a href="/" className="back-link">Back to Home</a>
        </div>
      </div>
      
      <style>{`
        .login-wrapper {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(10px);
        }
        
        .login-container {
          width: 100%;
          max-width: 400px;
          padding: 30px;
          background-color: rgba(0, 0, 0, 0.5);
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
        
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-top: 20px;
        }
        
        h2 {
          color: white;
          text-align: center;
          margin-bottom: 20px;
          font-family: 'Manrope', sans-serif;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .form-group label {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }
        
        .form-group input {
          padding: 12px 15px;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          background-color: rgba(0, 0, 0, 0.3);
          color: white;
          font-size: 16px;
          transition: all 0.3s ease;
        }
        
        .form-group input:focus {
          outline: none;
          border-color: rgba(255, 255, 255, 0.5);
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
        }
        
        .login-button {
          padding: 12px;
          background-color: rgba(66, 133, 244, 0.7);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          font-size: 16px;
          transition: all 0.3s ease;
        }
        
        .login-button:hover {
          background-color: rgba(66, 133, 244, 0.9);
          transform: translateY(-2px);
        }
        
        .login-error {
          background-color: rgba(234, 67, 53, 0.2);
          color: #ff6b6b;
          padding: 10px 15px;
          border-radius: 5px;
          font-size: 14px;
          border-left: 3px solid #ff6b6b;
        }
        
        .login-footer {
          margin-top: 25px;
          text-align: center;
        }
        
        .back-link {
          color: rgba(255, 255, 255, 0.6);
          text-decoration: none;
          font-size: 14px;
          transition: color 0.3s ease;
        }
        
        .back-link:hover {
          color: white;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default Login; 